import React, { useEffect, useRef, useState, useCallback } from "react";
import NotificationAlert from "react-notification-alert";
import { Spinner } from "reactstrap";
import { Button } from "ui-kit-takeat";
import { useAuth } from "context/AuthContext";
import {
  getBanners,
  createBanner,
  updateBanner,
  deleteBanner,
} from "services/bannersService";
import BannerCard from "./BannerCard";
import BannerFormModal from "./BannerFormModal";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import { canManageBanners } from "./constants";
import { PageHeader, BannersGrid, CenterMessage } from "./styles";

export const Banners = () => {
  const notificationAlert = useRef(null);
  const { tokenGd, user } = useAuth();
  const canManage = canManageBanners(user?.email);

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [saving, setSaving] = useState(false);

  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const notify = useCallback((place, color, message) => {
    notificationAlert.current.notificationAlert({
      place,
      message: (
        <div>
          <div>{message}</div>
        </div>
      ),
      type: color,
      icon: "now-ui-icons ui-1_bell-53",
      autoDismiss: 7,
    });
  }, []);

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await getBanners(tokenGd);
      setBanners(data);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [tokenGd]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const openCreate = useCallback(() => {
    setSelectedBanner(null);
    setIsFormOpen(true);
  }, []);

  const openEdit = useCallback((banner) => {
    setSelectedBanner(banner);
    setIsFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setSelectedBanner(null);
  }, []);

  const extractError = (err) =>
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    "Ocorreu um erro. Tente novamente.";

  const handleSave = useCallback(
    async (payload) => {
      setSaving(true);
      try {
        if (selectedBanner) {
          await updateBanner(selectedBanner.id, payload, tokenGd);
          notify("tr", "success", "Banner atualizado com sucesso!");
        } else {
          await createBanner(payload, tokenGd);
          notify("tr", "success", "Banner criado com sucesso!");
        }
        closeForm();
        fetchBanners();
      } catch (err) {
        notify("tr", "danger", extractError(err));
      } finally {
        setSaving(false);
      }
    },
    [selectedBanner, tokenGd, notify, closeForm, fetchBanners]
  );

  const confirmDelete = useCallback(async () => {
    if (!bannerToDelete) return;
    setDeleting(true);
    try {
      await deleteBanner(bannerToDelete.id, tokenGd);
      notify("tr", "success", "Banner excluído com sucesso!");
      setBannerToDelete(null);
      fetchBanners();
    } catch (err) {
      notify("tr", "danger", extractError(err));
    } finally {
      setDeleting(false);
    }
  }, [bannerToDelete, tokenGd, notify, fetchBanners]);

  return (
    <>
      <NotificationAlert ref={notificationAlert} />
      <div className="content">
        <PageHeader>
          <h3>Banners</h3>
          <Button
            title="Criar banner"
            buttonColor="#28a745"
            onClick={openCreate}
            disabled={!canManage}
          />
        </PageHeader>

        {loading ? (
          <CenterMessage>
            <Spinner color="secondary" />
            <span>Carregando banners...</span>
          </CenterMessage>
        ) : error ? (
          <CenterMessage>
            <span>Não foi possível carregar os banners.</span>
            <Button title="Tentar de novo" buttonColor="#007bff" onClick={fetchBanners} />
          </CenterMessage>
        ) : banners.length === 0 ? (
          <CenterMessage>
            <span>Nenhum banner cadastrado ainda.</span>
            <Button
              title="Criar o primeiro"
              buttonColor="#28a745"
              onClick={openCreate}
              disabled={!canManage}
            />
          </CenterMessage>
        ) : (
          <BannersGrid>
            {banners.map((banner) => (
              <BannerCard
                key={banner.id}
                banner={banner}
                onEdit={openEdit}
                onDelete={setBannerToDelete}
                canManage={canManage}
              />
            ))}
          </BannersGrid>
        )}
      </div>

      <BannerFormModal
        isOpen={isFormOpen}
        toggle={closeForm}
        banner={selectedBanner}
        onSave={handleSave}
        saving={saving}
      />

      <ConfirmDeleteModal
        isOpen={!!bannerToDelete}
        toggle={() => setBannerToDelete(null)}
        banner={bannerToDelete}
        onConfirm={confirmDelete}
        deleting={deleting}
      />
    </>
  );
};

export default Banners;
